import Link from 'next/link'
import formatDate from 'date-fns/format'
import {map, isString, get, pick, omit, isInt} from '@cullylarson/f'
import {getServerConfig} from '@@client/config'
import {getBlogArticleExcerpts, getBlogArticlesNumTotal} from '@@client/content/api'
import Layout from '@@client/app/Layout'
import GeneralPage, {Header, Content} from '@@client/app/GeneralPage'
import Markdown from '@@client/components/Markdown'
import Img from '@@client/components/Img'
import Pagination from '@@client/components/Pagination'
import {getServersideOffsetInfo, getClientsideOffsetInfo} from '@@client/blog/blog-helper'

const ExcerptByKind = ({entity}) => {
    if(entity.kind === 'server-side') {
        return (<Markdown>{entity.excerpt}</Markdown>)
    }
    else {
        return (<div dangerouslySetInnerHTML={{__html: entity.excerpt}} />)
    }
}

const ExcerptPhoto = ({entity}) => {
    if(isString(entity.featuredImage)) {
        return <Img src={entity.featuredImage} />
    }
    else if(!entity.featuredImage.url) {
        return null
    }
    else {
        return <Img alt={entity.featuredImage.alt} src={entity.featuredImage.url} />
    }
}

const Excerpt = ({articleInfo}) => {
    const url = ['/blog', articleInfo.slug].join('/')

    return (
        <>
            <div className='container'>
                <div className='content'>
                    <div className='the-date'>{articleInfo.dateHuman}</div>
                    <h2><Link href={url}><a>{articleInfo.title}</a></Link></h2>
                    <div className='excerpt'><ExcerptByKind entity={articleInfo} /></div>
                    <div className='actions'>
                        <Link href={url}><a className='btn btn--yellow'>Read Article</a></Link>
                    </div>
                </div>
                <div className='photo'>
                    <Link href={url}><a><ExcerptPhoto entity={articleInfo} /></a></Link>
                </div>
            </div>
            <style jsx>
                {`
                    @import '@css/variables.css';

                    .container {
                        margin-bottom: 40px;
                        padding-bottom: 40px;
                        border-bottom: 1px solid $c--blue--light-gray;
                        display: grid;
                        grid-gap: 20px;

                        @media(--lg) { grid-template-columns: 1fr 0.9fr; }

                        &:last-child {
                            padding-bottom: 0;
                            margin-bottom: 0;
                            border-bottom: 0;
                        }
                    }

                    .content {
                        order: 2;

                        @media(--lg) { order: unset; }
                    }

                    .excerpt {
                        margin-bottom: 20px;
                    }

                    .photo {
                        order: 1;

                        @media(--lg) { order: unset; }
                    }

                    h2 {
                        margin-top: 0;
                        font-size: $size--medium;
                        text-transform: uppercase;

                        a {
                            font-weight: $weight--bold;
                            text-decoration: none;
                        }
                    }

                    .the-date {
                        font-style: italic;
                        font-size: $size--smaller;
                    }
                `}
            </style>
        </>
    )
}

export default function Page({articles, numArticles, page, perPage}) {
    return (
        <Layout title='The Recycled Part'>
            <GeneralPage>
                <Header title='The Recycled Part'>
                    <p>Autowrecking.com’s blog about car and truck care, DIY tips, humor, and advice from auto recyclers who know.</p>
                </Header>
                <Content>
                    {articles.map((info, slug) => <Excerpt key={slug} articleInfo={info} />)}

                    <Pagination
                        page={page}
                        perPage={perPage}
                        totalItems={numArticles}
                        pageQueryParamName='p'
                    />
                </Content>
            </GeneralPage>
        </Layout>
    )
}

async function getClientsideArticles(num, offset) {
    // dynamically importing in hopes that this won't end up in the client-side bundle. seems to have worked.
    const articles = Object.values((await import('@@client/blog/blog-data')).default)

    return num === 0
        ? {articles: [], numTotal: articles.length}
        : {
            articles: articles.slice(offset, offset + num),
            numTotal: articles.length,
        }
}

// basically converts server-side article to a structure the rest of the app is familiar with
function normalizeServersideArticle(entity) {
    return {
        ...pick(['slug', 'title', 'excerpt'], entity),
        dateHuman: formatDate(new Date(entity.articleDate), 'EEEE, MMMM do, yyyy'),
        featuredImage: {
            url: get(['featuredImage', 'formats', 'small', 'url'], null, entity),
            alt: get(['featuredImage', 'alternativeText'], null, entity),
        },
        kind: 'server-side',
    }
}

function normalizeClientsideArticle(entity) {
    return {
        ...omit(['content', 'created'], entity),
        kind: 'client-side',
    }
}

function getPage(ctx) {
    const page = ctx.query?.p || 1

    return isInt(page)
        ? page
        : 1
}

// we currently have some server-side and some client-side blog articles. this function will fetch the server-side articles first, and then fetch the
// client-side articles if we run out of server-side articles. eventually we'll move all of the blog articles to the CMS and won't need to do this.
export async function getServerSideProps(ctx) {
    const config = getServerConfig()

    // this pagination spans server-side and client-side (the user won't know the difference)
    const perPage = 20
    const page = getPage(ctx)

    // find out how many server-side articles we have
    const ssNumTotalResult = (await getBlogArticlesNumTotal(config.content.apiUrl))

    if(!ssNumTotalResult.success) {
        return {
            props: {
                articles: [],
                perPage,
                page,
                numArticles: 0,
            },
        }
    }

    const ssNumTotal = ssNumTotalResult.numTotal

    const ssOffsetInfo = getServersideOffsetInfo({ssNumTotal, page, perPage})
    const csOffsetInfo = getClientsideOffsetInfo({ssNumTotal, page, perPage})

    const [ssArticles, csArticlesResult] = await Promise.all([
        ssOffsetInfo.num === 0
            ? Promise.resolve([])
            : getBlogArticleExcerpts(config.content.apiUrl, ssOffsetInfo)
                .then(result => {
                    return result.success
                        ? result.entities
                        : []
                }),
        getClientsideArticles(csOffsetInfo.num, csOffsetInfo.offset),
    ])

    return {
        props: {
            articles: [
                ...map(normalizeServersideArticle, ssArticles),
                ...map(normalizeClientsideArticle, csArticlesResult.articles),
            ],
            perPage,
            page,
            numArticles: (ssNumTotal + csArticlesResult.numTotal),
        },
    }
}
